import * as moment from "moment";
import { Component, OnInit, OnDestroy, Input } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, FormArray } from "@angular/forms";
import { LoginService } from "../login.service";
import { SettingsService } from "../settings.service";
import { Patient } from "../patients/patient";
import { BillingService } from "../billing.service";
import { PatientsService } from "../patients.service";
import { HttpClient } from "@angular/common/http";
import { Subscription, Subject } from "rxjs";
import { takeUntil, switchMap, delay } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { baseCountries, israelCountry, Country } from "../patients/country";

@Component({
    selector: "app-invoice",
    templateUrl: "./invoice.component.html",
    styleUrls: ["./invoice.component.scss"],
    standalone: false
})
export class InvoiceComponent implements OnInit, OnDestroy {
  @Input("currentPatient") currentPatient: Patient;

  invoicesDataSub: Subscription;

  newInvoice = false;
  invoiceForm: UntypedFormGroup;
  cancellationForm: UntypedFormGroup;
  columns = new Array(10);
  paymentMethods = [
    { label: "כרטיס אשראי", value: 3 },
    { label: "כרטיס אשראי + מזומן", value: 33 },
    { label: "העברה בנקאית", value: 4 },
    { label: "העברה בנקאית + מזומן", value: 44 },
    { label: "מזומן", value: 1 },
    { label: "שיקים", value: 2 },
    { label: "מזומן + שיקים", value: 22 },
    // { label: "אחר", value: 11 },
  ];

  documentTypes = [
    // { label: "חשבון עסקה", value: 300 },
    { label: "חשבונית מס", value: 305 },
    { label: "חשבונית מס / קבלה", value: 320 },
    { label: "חשבונית זיכוי", value: 330 },
    { label: "קבלה", value: 400 },
    { label: "הצעת מחיר", value: 10 },
  ];

  numberOfPayments: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  creditPayments: number[] = [1, 2, 3, 4, 5, 6];
  months: string[] = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
  currentYear: number = new Date().getFullYear();
  years: number[] = [];
  rowsArray: number[] = [];
  availableExams = [];
  produceInvoiceBtn: string = this.translate.instant("billing.gui.buttons.Create New Document");
  resultMessage: string = this.translate.instant("billing.gui.labels.New Document Created");

  invoicesData: any;
  sortDirection = "asc";
  currentSortColumn: string | null = null;

  displayWarning: boolean = false;
  warningMessage: string = "";

  selectedInvoice: any = null;
  showInvoices: boolean = true;
  isPDFLoading: boolean = true;
  showCloseInvoice: boolean = false;
  result: boolean = false;
  isLoading: boolean = false;
  isPreviewLoading: boolean = false;
  private destroy$ = new Subject<void>();
  showFreeText: boolean = false;
  public countries: Country[] = [...baseCountries];
  public filteredCountries: Country[] = [];
  public selectedCountryName: string = "";

  // Generate Invoice from Receipt properties
  showGenerateInvoicePopup: boolean = false;
  selectedReceiptForInvoice: any = null;
  receiptChecks: any[] = [];
  loadingCheckId: number | null = null;
  emailLoadingId: string | null = null;
  receiptUninvoicedChecksMap: Map<number, boolean> = new Map(); // Track which receipts have uninvoiced checks
  receiptsLoadingInProgress: Set<number> = new Set(); // Track which receipts are currently being loaded

  constructor(
    private formGroupBuilder: UntypedFormBuilder,
    public loginService: LoginService,
    public settingsService: SettingsService,
    public billingService: BillingService,
    public patientService: PatientsService,
    private http: HttpClient,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const { First, Last, Email, ID, PID } = this.currentPatient;

    this.resetInvoiceForm();

    this.resetCancellationForm();
    this.invoiceForm
      .get("numberOfPayments")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.updateChecks(value);
      });

    this.settingsService.getExams().then((data) => {
      this.availableExams = data;
    });

    this.invoiceForm
      .get("documentType")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const paymentControl = this.invoiceForm.get("paymentMethod");
        if (value == 10) {
          paymentControl.setValue("");
          if (this.items.length === 0) {
            this.addItem();
          }
          this.syncQuotationTotals();
        } else if (!paymentControl.value) {
          paymentControl.setValue(this.paymentMethods[2].value);
        }
      });

    this.invoiceForm
      .get("paymentMethod")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.resetPaymentForms();

        if (value == "2") {
          const price = this.invoiceForm.get("price").value || 0;
          const numberOfPayments = this.invoiceForm.get("numberOfPayments").value;
          const amounts = this.splitPrice(price, numberOfPayments);

          // Clear existing checks
          while (this.checks.length > 0) {
            this.checks.removeAt(0);
          }

          // Add checks with the split amounts
          amounts.forEach((amount) => {
            this.addCheck(amount);
          });

          this.validateTotalAmount();
        } else if (value == "22") {
          this.invoiceForm.get("cash").setValue(0);
          this.updateChecks(this.invoiceForm.get("numberOfPayments").value);
          this.validateTotalAmount();
        } else {
          this.invoiceForm.get("cash").setValue(0);
        }
      });
    if (this.currentPatient) {
      const { First, Last, Email, ID, country } = this.currentPatient;
      this.invoiceForm.patchValue({ firstName: First, lastName: Last, ID, email: Email });

      const matched = this.countries.find((c) => c.code === country || c.name === country);
      this.invoiceForm.patchValue({
        country: matched?.code || "IL",
        countryName: matched?.name || "",
      });
    }

    this.invoicesDataSub = this.billingService
      .getInvoices(PID)
      .pipe(takeUntil(this.destroy$))
      .subscribe((invoices) => {
        this.invoicesData = invoices;
      });

    if (this.loginService.premissions?.Appointments && !this.loginService?.premissions?.ApptsByRoom) {
      this.getTodaysFirstAppointment();
    } else {
      // this.getTodaysFirstExam();
    }

    this.setDefaultFormValues();

    this.translate.setDefaultLang("he-IL");
    this.translate.use("he-IL");

    this.invoiceForm.get("examType").valueChanges.subscribe((value) => {
      if (this.showFreeText) {
        this.invoiceForm.get("examType_native").setValue(value, { emitEvent: false });
      }
    });

    this.invoiceForm
      .get("countryName")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((input: string) => {
        const lower = input?.toLowerCase() || "";
        this.filteredCountries = this.countries.filter(
          (c) => c.name.toLowerCase().includes(lower) || c.code.toLowerCase().includes(lower)
        );

        const selected = this.countries.find((c) => c.name === input || c.code === input);

        if (selected) {
          this.invoiceForm.get("country").setValue(selected.code);
          this.invoiceForm.get("countryName").setValue(selected.name, { emitEvent: false });
        } else if (!input?.trim()) {
          this.invoiceForm.get("country").setValue(null);
        }
      });

    this.filteredCountries = [...this.countries];

    // Set showFreeText to true for business clients
    if (this.currentPatient?.isBusinessClient) {
      this.showFreeText = true;
    }
  }
  /*!!!! need to unsubsribe to all of the subscriptions so that there won't be a leak!!!*/
  ngOnDestroy() {
    if (this.invoicesDataSub) {
      this.invoicesDataSub.unsubscribe();
    }
    this.destroy$.next(); // Emit a value to notify all subscriptions
    this.destroy$.complete(); // Complete the subject to prevent further emissions
  }

  // Form Groups:
  resetInvoiceForm(): void {
    this.invoiceForm = this.formGroupBuilder.group({
      firstName: [""],
      lastName: [""],
      ID: [""],
      email: [""],
      documentType: [""],
      doctor: [""],
      examName: [""],
      examType: [""],
      examType_native: [""],
      price: [""],
      paymentMethod: [""],
      numberOfPayments: [1],
      country: ["IL"], // the code that goes to backend
      countryName: [""], // the display name (used in UI)
      checks: this.formGroupBuilder.array([]),
      bankTransfer: this.formGroupBuilder.group({
        bankNumber: [""],
        bankBranch: [""],
        accountNumber: [""],
        date: [new Date()],
        amount: [""],
      }),
      creditCard: this.formGroupBuilder.group({
        cardNumber: [""],
        numberOfPayments: [1],
        transactionId: "",
        ccAmt: "",
      }),
      cash: [""],
      items: this.formGroupBuilder.array([]),
      quotationRemarks: [""],
      quotationTerms: [""],
    });

    this.invoiceForm.get("items").valueChanges.subscribe(() => this.syncQuotationTotals());

    this.updateChecks(this.invoiceForm.get("numberOfPayments").value);
    this.populateYears();
  }

  disableCreditCardForm(): void {
    this.invoiceForm.get("creditCard").disable();
  }

  resetCancellationForm(): void {
    this.cancellationForm = this.formGroupBuilder.group({
      description: [""],
      price: [""],
    });
  }

  resetPaymentForms() {
    // Clear the checks form array
    const checks = this.invoiceForm.get("checks") as FormArray;
    while (checks.length !== 0) {
      checks.removeAt(0);
    }

    // Reset bankTransfer form group
    this.invoiceForm.get("bankTransfer").reset({
      bankNumber: "",
      bankBranch: "",
      accountNumber: "",
      date: new Date(),
      amount: "",
    });

    // Reset creditCard form group
    this.invoiceForm.get("creditCard").reset({
      cardNumber: "",
      numberOfPayments: 1,
      cardHolderId: "",
      transactionId: "",
      ccAmt: "",
    });

    // Optionally, reset cash if required
    // this.invoiceForm.get("cash").reset("");
  }

  // // TODO: MAKE THE CASH editable when using cash+checks
  updateChecks(numberOfPayments: number, skipcalculation = false): void {
    const price = this.invoiceForm.get("price").value || 0;
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    const checksArray = this.checks as FormArray;
    let cashAmount = this.invoiceForm.get("cash").value;

    const amounts = this.splitPrice(price - cashAmount, numberOfPayments);

    // Adjust number of checks
    if (numberOfPayments > checksArray.length) {
      while (checksArray.length < numberOfPayments) {
        this.addCheck();
      }
    } else {
      while (checksArray.length > numberOfPayments) {
        checksArray.removeAt(checksArray.length - 1);
      }
    }

    // Get the first check values
    const firstCheckValues = checksArray.length > 0 ? checksArray.at(0).value : null;

    // Set amounts and copy specific fields from the first check to other checks
    checksArray.controls.forEach((check, index) => {
      if (index === 0) {
        if (!skipcalculation) check.get("amount").setValue(amounts[index], { emitEvent: false });

        // Add listener for the first checkNumber field
        check
          .get("checkNumber")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.updateCheckNumbers(value);
          });

        // Add listeners for the first check fields
        check
          .get("bankNumber")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.copyFieldToRemainingChecks("bankNumber", value);
          });
        check
          .get("bankBranch")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.copyFieldToRemainingChecks("bankBranch", value);
          });
        check
          .get("accountNumber")
          .valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe((value) => {
            this.copyFieldToRemainingChecks("accountNumber", value);
          });
      } else if (firstCheckValues) {
        if (!check.get("bankNumber").value) {
          check.get("bankNumber").setValue(firstCheckValues.bankNumber, { emitEvent: false });
        }
        if (!check.get("bankBranch").value) {
          check.get("bankBranch").setValue(firstCheckValues.bankBranch, { emitEvent: false });
        }
        if (!check.get("accountNumber").value) {
          check.get("accountNumber").setValue(firstCheckValues.accountNumber, { emitEvent: false });
        }
        if (!skipcalculation) check.get("amount").setValue(amounts[index], { emitEvent: false });
      }
    });

    // Revalidate the total amount after updates
    this.validateTotalAmount();
  }

  copyFieldToRemainingChecks(fieldName: string, value: any): void {
    const checksArray = this.checks as FormArray;
    checksArray.controls.forEach((check, index) => {
      if (
        index !== 0 &&
        check.get(fieldName).value == (value + "").slice(0, (check.get(fieldName).value + "").length)
      ) {
        check.get(fieldName).setValue(value, { emitEvent: false });
      }
    });
  }

  updateCheckNumbers(startingNumber: number): void {
    const checksArray = this.checks as FormArray;

    // Update checkNumber fields for subsequent checks
    checksArray.controls.forEach((check, index) => {
      if (index > 0) {
        check.get("checkNumber").setValue(startingNumber + index, { emitEvent: false });
      }
    });
  }

  addCheck(amount: number = 0): void {
    const baseDate = new Date();
    if (this.checks.length > 0) {
      const lastDate = new Date(this.checks.at(this.checks.length - 1).get("date").value);
      lastDate.setMonth(lastDate.getMonth() + 1);
      baseDate.setTime(lastDate.getTime());
    }
    const check = this.formGroupBuilder.group({
      bankNumber: [""],
      bankBranch: [""],
      accountNumber: [""],
      checkNumber: [""],
      date: [baseDate],
      amount: [amount],
    });
    this.checks.push(check);
  }

  get checks(): FormArray {
    return this.invoiceForm.get("checks") as FormArray;
  }

  populateYears() {
    for (let i = 0; i < 10; i++) {
      this.years.push(this.currentYear + i);
    }
  }

  // Payment Methods:

  isCash(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 1 || paymentMethod == 33 || paymentMethod == 44; // keeping it loose typed for now
  }

  isChecks(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 2;
  }

  isChecksCash(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 22;
  }

  isBankTransferCash(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 44;
  }

  isBankTransfer(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 4 || paymentMethod == 44;
  }

  isCreditCard(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 3 || paymentMethod == 33;
  }

  isCashCard(): boolean {
    const paymentMethod = this.invoiceForm.get("paymentMethod").value;
    return paymentMethod == 33;
  }

  // Data:

  // For a multiple doctors clinic:
  getDoctors(): any {
    if (this.loginService?.premissions?.ApptsByRoom && this.loginService?.ClinicID === 19) {
      let setDrSilber = this.loginService.clinicUsers.find((user) => user.UID === 434);

      this.invoiceForm.get("doctor").setValue(setDrSilber);
      // TODO: find a better way
      // Filter all users exepct Dr. Silber and Test
      return (this.loginService.clinicUsers = this.loginService.clinicUsers.filter(
        (doctor) => doctor.UID === 434 || doctor.UID === 196
      ));
    } else {
      return this.loginService.clinicUsers;
    }
  }

  // For a single doctor clinic:
  getDoctor(): any {
    return this.loginService.userBilling;
  }

  isQuotation(): boolean {
    return this.invoiceForm.get("documentType").value == 10;
  }

  get items(): FormArray {
    return this.invoiceForm.get("items") as FormArray;
  }

  addItem(): void {
    this.items.push(
      this.formGroupBuilder.group({
        examType: [""],
        description: [""],
        quantity: [1],
        price: [""],
      })
    );
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
    if (this.items.length === 0) {
      this.addItem();
    }
  }

  itemTotal(index: number): number {
    const item = this.items.at(index).value;
    return (Number(item.quantity) || 0) * (Number(item.price) || 0);
  }

  get quotationTotal(): number {
    return this.items.controls.reduce((sum, _, i) => sum + this.itemTotal(i), 0);
  }

  onItemExamChange(event: Event, index: number): void {
    const selectEl = event.target as HTMLSelectElement;
    const option = selectEl.options[selectEl.selectedIndex];
    const description = this.structureDescription({
      examName: option.text,
      examType: option.getAttribute("data-examtype"),
      HMO: this.currentPatient.HMO,
      custom: option.getAttribute("data-custom"),
    });
    this.items.at(index).get("description").setValue(description);
  }

  private syncQuotationTotals(): void {
    if (!this.isQuotation()) return;
    const descriptions = this.items.controls
      .map((control) => control.value.description)
      .filter((text) => !!text);
    this.invoiceForm.get("price").setValue(this.quotationTotal, { emitEvent: false });
    this.invoiceForm.get("examName").setValue(descriptions.join(", "), { emitEvent: false });
  }

  isFormValid(): boolean {
    if (!this.isCashCard() && !this.isChecksCash() && !this.isChecks() && !this.isBankTransferCash())
      return this.getMissingFields().length === 0;
    //also check the sums
    else return this.getMissingFields().length === 0 && this.validateTotalAmount();
  }

  getMissingFields(): string[] {
    const invoiceForm = this.invoiceForm.value;
    const requiredFields = [
      { name: "firstName", label: "First Name" },
      { name: "ID", label: "ID" },
      { name: "documentType", label: "Document Type" },
      { name: "doctor", label: "Doctor" },
    ];

    if (this.isQuotation()) {
      this.items.value.forEach((item, index) => {
        if (!item.description) requiredFields.push({ name: `__item${index}Description`, label: `Item ${index + 1} Description` });
        if (!item.quantity || item.quantity < 1) requiredFields.push({ name: `__item${index}Quantity`, label: `Item ${index + 1} Quantity` });
        if (!item.price) requiredFields.push({ name: `__item${index}Price`, label: `Item ${index + 1} Price` });
      });
    } else {
      requiredFields.push(
        { name: "examName", label: "Description" },
        { name: "examType", label: "Exam Type" },
        { name: "price", label: "Price" },
        { name: "paymentMethod", label: "Payment Method" }
      );
    }

    // Only require Last Name if not a business client
    if (!this.currentPatient?.isBusinessClient) {
      requiredFields.splice(1, 0, { name: "lastName", label: "Last Name" });
    }

    const missingFields = [];

    // Check if required fields are filled
    requiredFields.forEach((field) => {
      if (!invoiceForm[field.name]) {
        missingFields.push(field.label);
      }
    });

    if (this.currentPatient?.IsID === 0 && !invoiceForm.country) {
      missingFields.push("Country");
    }
    // If payment method is שיקים:
    if (invoiceForm.paymentMethod == "2") {
      const checksArray = this.checks.value;
      checksArray.forEach((check, index) => {
        if (!check.bankNumber) missingFields.push(`Check ${index + 1} Bank Number`);
        if (!check.bankBranch) missingFields.push(`Check ${index + 1} Bank Branch`);
        if (!check.accountNumber) missingFields.push(`Check ${index + 1} Account Number`);
        if (!check.checkNumber) missingFields.push(`Check ${index + 1} Check Number`);
        if (!check.date) missingFields.push(`Check ${index + 1} Date`);
        if (check.amount === null) missingFields.push(`Check ${index + 1} Amount`);
      });
    }

    // If payment method is שיקים + מזומן:
    if (invoiceForm.paymentMethod == "22") {
      const checksArray = this.checks.value;
      checksArray.forEach((check, index) => {
        if (!check.bankNumber) missingFields.push(`Check ${index + 1} Bank Number`);
        if (!check.bankBranch) missingFields.push(`Check ${index + 1} Bank Branch`);
        if (!check.accountNumber) missingFields.push(`Check ${index + 1} Account Number`);
        if (!check.checkNumber) missingFields.push(`Check ${index + 1} Check Number`);
        if (!check.date) missingFields.push(`Check ${index + 1} Date`);
        if (check.amount === null) missingFields.push(`Check ${index + 1} Amount`);
      });
      // Check if cash has value
      if (!invoiceForm.cash) {
        missingFields.push("Cash");
      }
    }

    // If payment method is העברה בנקאית:
    if (invoiceForm.paymentMethod == "4") {
      const bankTransfer = this.invoiceForm.get("bankTransfer").value;
      const requiredBankTransferFields = [
        { name: "bankNumber", label: "Bank Transfer Bank Number" },
        { name: "bankBranch", label: "Bank Transfer Bank Branch" },
        { name: "accountNumber", label: "Bank Transfer Account Number" },
        { name: "date", label: "Bank Transfer Date" },
        //{ name: "amount", label: "Bank Transfer Amount" },
      ];
      requiredBankTransferFields.forEach((field) => {
        if (!bankTransfer[field.name]) {
          missingFields.push(field.label);
        }
      });
    }

    // If payment method is כרטיס אשראי: with cash
    if (invoiceForm.paymentMethod == "33" || invoiceForm.paymentMethod == "3") {
      const creditCard = this.invoiceForm.get("creditCard").value;
      const requiredCreditCardFields = [
        { name: "cardNumber", label: "Credit Card Number" },
        // { name: "transactionId", label: "Transaction Code" }, // commenting it out - making it optional
      ];
      if (invoiceForm.paymentMethod == "33") {
        requiredCreditCardFields.push({ name: "ccAmt", label: "Amount on Credit Card" });
      }
      requiredCreditCardFields.forEach((field) => {
        if (!creditCard[field.name]) {
          missingFields.push(field.label);
        }
      });
    }

    // If payment method is העברה בנקאית + מזומן:
    if (invoiceForm.paymentMethod == "44" || invoiceForm.paymentMethod == "4") {
      const bankTransfer = this.invoiceForm.get("bankTransfer").value;
      const requiredBankTransferFields = [
        { name: "bankNumber", label: "Bank Transfer Bank Number" },
        { name: "bankBranch", label: "Bank Transfer Bank Branch" },
        { name: "accountNumber", label: "Bank Transfer Account Number" },
        { name: "date", label: "Bank Transfer Date" },
      ];

      requiredBankTransferFields.forEach((field) => {
        if (!bankTransfer[field.name]) {
          missingFields.push(field.label);
        }
      });
    }

    return missingFields;
  }

  produceInvoice() {
    this.normalizeAllPaymentDatesForSubmit();

    const { PID } = this.currentPatient;
    this.result = false;
    this.isLoading = true;

    this.billingService.produceInvoice(this.invoiceForm, this.currentPatient).subscribe(
      (invoice) => {
        if (invoice) {
          const formEmail = (this.invoiceForm.get("email").value || "").trim();
          if (formEmail) {
            this.currentPatient.Email = formEmail;
          }
          this.billingService.getInvoices(PID).subscribe(
            (invoices) => {
              this.result = true;
              this.isLoading = false;
              this.invoicesData = invoices;
              this.newInvoice = false; //close the make invoice data
            },
            (error) => {
              this.isLoading = false;
              console.error("Failed to get invoices:", error);
            }
          );
        }
      },
      (error) => {
        this.isLoading = false;
        console.error("Failed to produce invoice:", error);
      }
    );
  }

  async previewDocument() {
    this.isPreviewLoading = true;
    this.normalizeAllPaymentDatesForSubmit();

    this.billingService.previewDocument(this.invoiceForm, this.currentPatient).subscribe(
      (preview) => {
        if (preview) {
          const { file } = preview;

          if (file) {
            const byteCharacters = atob(file);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "application/pdf" });

            const blobUrl = URL.createObjectURL(blob);
            this.isPreviewLoading = false;

            window.open(blobUrl, "_blank");
          } else {
            this.isPreviewLoading = false;
            console.error("No file found in the response.");
          }
        }
      },
      (error) => {
        this.isPreviewLoading = false;
        console.error("Failed to produce invoice:", error);
      }
    );
  }

  produceCreditInvoice() {
    const { PID } = this.currentPatient;
    this.result = false;
    this.isLoading = true;

    this.billingService
      .produceCreditInvoice(this.selectedInvoice, this.cancellationForm, this.currentPatient)
      .subscribe(
        (invoice) => {
          if (invoice) {
            this.billingService.getInvoices(PID).subscribe(
              (invoices) => {
                this.result = true;
                this.isLoading = false;
                this.invoicesData = invoices;
                this.toggleInvoices();
              },
              (error) => {
                this.isLoading = false;
                console.error("Failed to produce invoice:", error);
              }
            );
          }
        },
        (error) => {
          this.isLoading = false;
          console.error("Failed to produce credit invoice:", error);
        }
      );
  }

  produceCancelCreditReceipt() {
    const { PID } = this.currentPatient;
    this.result = false;
    this.isLoading = true;

    this.billingService
      .produceCancelCreditReceipt(this.selectedInvoice, this.cancellationForm, this.currentPatient)
      .subscribe(
        (invoice) => {
          if (invoice) {
            this.billingService.getInvoices(PID).subscribe(
              (invoices) => {
                this.result = true;
                this.isLoading = false;
                this.invoicesData = invoices;
                this.toggleInvoices();
              },
              (error) => {
                this.isLoading = false;
                console.error("Failed to get invoices:", error);
              }
            );
          }
        },
        (error) => {
          this.isLoading = false;
          console.error("Failed to produce cancel credit receipt:", error);
        }
      );
  }

  produceCancelReceipt() {
    const { PID } = this.currentPatient;
    this.result = false;
    this.isLoading = true;

    this.billingService
      .produceCancelReceipt(this.selectedInvoice, this.cancellationForm, this.currentPatient)
      .subscribe(
        (invoice) => {
          if (invoice) {
            this.billingService.getInvoices(PID).subscribe(
              (invoices) => {
                this.result = true;
                this.isLoading = false;
                this.invoicesData = invoices;
                this.toggleInvoices();
              },
              (error) => {
                this.isLoading = false;
                console.error("Failed to get invoices:", error);
              }
            );
          }
        },
        (error) => {
          this.isLoading = false;
          console.error("Failed to produce cancel receipt:", error);
        }
      );
  }

  originDocumentViewed(invoiceIndex: any) {
    const { PID } = this.currentPatient;

    this.billingService.originDocumentViewed(invoiceIndex).subscribe(
      (updatedInvoice) => {
        this.billingService.getInvoices(PID).subscribe(
          (invoices) => {
            this.invoicesData = invoices;
          },
          (error) => {
            this.isLoading = false;
            console.error("Failed to get invoices:", error);
          }
        );
      },
      (error) => {
        this.isLoading = false;
        console.error("Failed to set origin document as viewed:", error);
      }
    );
  }

  closeDocument(invoiceIndex: any, documentID: string) {
    const { PID } = this.currentPatient;
    this.result = false;
    this.isLoading = true;
    try {
      this.billingService.closeInvoice(invoiceIndex, documentID).subscribe((updatedInvoice) => {
        this.billingService.getInvoices(PID).subscribe((invoices) => {
          this.result = true;
          this.isLoading = false;
          this.invoicesData = invoices;
        });
      });
    } catch (error) {
      this.isLoading = false;
    }
  }

  // Gets today's first appointment
  getTodaysFirstAppointment() {
    const { PID } = this.currentPatient;

    this.billingService.getTodaysFirstAppointment(PID).subscribe(
      (appointment) => {
        if (appointment.length) {
          const { examType, examName, examTypeID, Price, DocUID, HMO, Insurance, Fetus, Visit, Funding } = appointment[0];
          const customExamName = this.structureDescription(appointment[0]);

          this.invoiceForm.get("examName").setValue(customExamName);
          this.invoiceForm.get("examType").setValue(examTypeID);
          this.invoiceForm.get("examType_native").setValue(examName||examType);

          // this.examChange();
          this.invoiceForm.get("price").setValue(Price);

          if (this.loginService.clinicUsers) {
            const doctors = this.getDoctors();
            let matchingDocs = doctors.find((user) => user.UID === DocUID);
            this.invoiceForm.get("doctor").setValue(matchingDocs);
          } else if (this.loginService.userBilling) {
            const doctor = this.getDoctor();
            let matchingDoc = doctor.find((user) => user.UID === DocUID);
            this.invoiceForm.get("doctor").setValue(matchingDoc);
          }
        }
      },
      (error) => {
        this.isLoading = false;
        console.error("Failed to get today's first appointment:", error);
      }
    );
  }

  structureDescription(appointmentData: any): string {
    let structuredDescription;
    let examFunding = "";
    let fetusNumber = "";
    const { examName, examType, custom, Price, DocUID, HMO, Insurance, Fetus, Funding } =
      appointmentData;

    if (!custom || custom == "false") {
      // if (Funding === "Private") {
      //   examFunding = `${this.settingsService.hmoMapping["Private"]}`;
      // } else {
      //   examFunding = `${this.settingsService.hmoMapping[HMO]} ${
      //     this.settingsService.insuranceMapping[Insurance]
      //       ? this.settingsService.insuranceMapping[Insurance]
      //       : ""
      //   }`;
      // }
      // if (Fetus === 1) {
      //   fetusNumber = `עובר יחיד`;
      // } else if (Fetus === 2) {
      //   fetusNumber = `תאומים`;
      // }
      const translatedExamType = this.translate.instant("exam.type.appointments." + examType);
      structuredDescription = `${translatedExamType ? translatedExamType : ""} ${
        examFunding ? examFunding : ""
      }  ${fetusNumber ? fetusNumber : ""} `;
    } else {
      structuredDescription = `${examType}`;
    }

    return structuredDescription;
  }

  openNewTab(link: string) {
    window.open(link, "_blank");
  }

  sortData(property: string) {
    if (this.currentSortColumn === property) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortDirection = "asc";
      this.currentSortColumn = property;
    }

    this.invoicesData.sort((a, b) => {
      const valueA = new Date(a[property]).getTime();
      const valueB = new Date(b[property]).getTime();

      return this.sortDirection === "asc" ? valueA - valueB : valueB - valueA;
    });
  }

  async openPdf(isPrinted: any, url: string) {
    if (isPrinted) {
      return;
    }

    await this.http.get(url, { responseType: "blob" }).subscribe(
      (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");
      },
      (error) => {
        console.error("Error downloading the PDF:", error);
      }
    );
  }

  splitPrice(price: number, numberOfPayments: number): number[] {
    const splittedAmounts: number[] = [];
    const roundedAmount = Math.ceil(price / numberOfPayments);
    const remainingAmount = price - roundedAmount * (numberOfPayments - 1);

    for (let i = 0; i < numberOfPayments - 1; i++) {
      splittedAmounts.push(roundedAmount);
    }
    splittedAmounts.push(remainingAmount);

    return splittedAmounts;
  }

  validateTotalAmount(): boolean {
    const cashAmount = this.invoiceForm.get("cash").value || 0;
    const totalPrice = this.invoiceForm.get("price").value || 0;
    var totalAmount;
    if (this.isChecksCash() || this.isChecks()) {
      const checksTotal = this.checks.controls
        .map((control) => control.value.amount || 0)
        .reduce((acc, amount) => acc + amount, 0);
      totalAmount = checksTotal + cashAmount;
    } else if (this.isBankTransferCash()) {
      totalAmount = cashAmount + (this.invoiceForm.get("bankTransfer").get("amount").value || 0);
    } else {
      //credit & cash
      totalAmount = cashAmount + (this.invoiceForm.get("creditCard").get("ccAmt").value || 0);
    }

    // console.log("Total of checks and cash:", totalAmount, "vs Total Price:", totalPrice);

    if (totalAmount !== totalPrice) {
      this.displayWarning = true;
      this.warningMessage = `The sum of all payments (${totalAmount}) does not match the total price (${totalPrice}).`;
      return false;
    } else {
      this.displayWarning = false;
      this.warningMessage = "";
      return true;
    }
  }

  selectInvoice(invoice: any): void {
    this.showCloseInvoice = false;
    const shouldDisable = this.disableCloseInvoiceButton(invoice.DocumentStatus, invoice.TypeName);
    if (shouldDisable) {
      return;
    }

    this.selectedInvoice = invoice;
    this.showInvoices = false; // Ensures that the list is hidden when viewing details
    this.displayPdfInline(invoice.CopyLink); // Initiates PDF loading
  }

  confirmCancelation(confirm: boolean): void {
    if (confirm) {
      this.closeDocument(this.selectedInvoice.AutoIndex, this.selectedInvoice.DocumentID);
    }
    // Clear and hide details
    this.selectedInvoice = null;
    this.showInvoices = true; // Reveals the invoice list again after confirm
  }

  toggleInvoices(): void {
    this.showInvoices = !this.showInvoices;
    if (!this.showInvoices) {
      // If hiding invoices, also hide details
      this.selectedInvoice = null;
    }
  }

  toggleCloseInvoicePopup(): void {
    this.showCloseInvoice = !this.showCloseInvoice;
    if (this.showCloseInvoice) {
      this.handleCancellationInputs();
    }
  }

  displayPdfInline(url: string): void {
    this.isPDFLoading = true;
    this.http.get(url, { responseType: "blob" }).subscribe(
      (blob) => {
        this.selectedInvoice.pdfUrl = URL.createObjectURL(blob);
        this.isPDFLoading = false;
      },
      (error) => {
        console.error("Error fetching the PDF:", error);
        this.isPDFLoading = false;
      }
    );
  }

  disableCloseInvoiceButton(status: number, invoiceTypeName: string) {
    const disableStatuses = [2, 3, 4];

    return disableStatuses.includes(status) || invoiceTypeName === "חשבונית זיכוי";
  }

  cancellationMessage(invoiceTypeName: string): string {
    let message = ``;
    switch (invoiceTypeName) {
      case `חשבונית מס`:
        message = `ביטול חשבונית מתבצע על ידי הפקת חשבונית זיכוי  \n להמשיך בתהליך?`;
        break;
      case `חשבונית מס / קבלה`:
        message = `ביטול המסמך מתבצע על ידי הפקת חשבונית זיכוי וקבלה בסכום שלילי \n להמשיך בתהליך?`;
        break;
      case `קבלה`:
        message = `ביטול קבלה מתבצע על ידי הפקת קבלה בסכום שלילי \n להמשיך בתהליך?`;
        break;
      default:
        message = `האם אתה בטוח שברצונך לזכות את ה-${invoiceTypeName}?`;
        break;
    }
    return message;
  }

  handleCancelPrice() {
    const price = this.cancellationForm.get("price").value;
    const invoicePrice = this.selectedInvoice.Price;

    if (price > invoicePrice) {
      return `סכום הזכוי לא יכול להיות יותר מהמסמך המקורי`;
    }

    return "";
  }

  disableCancellation(): boolean {
    const price = this.cancellationForm.get("price").value;
    const invoicePrice = this.selectedInvoice.Price;

    if (price > invoicePrice) {
      return true;
    }

    return false;
  }

  handleCancellationInputs(): string {
    const descriptionForm = this.cancellationForm.get("description");
    const priceForm = this.cancellationForm.get("price");

    const invoiceType = this.selectedInvoice.TypeName;
    const invoiceNumber = this.selectedInvoice.DocumentNumber;
    const invoiceSubject = this.selectedInvoice.Subject;
    const invoicePrice = this.selectedInvoice.Price;

    let message = ``;

    // Extract the part of the subject after removing 'חיוב'
    const subjectContent = invoiceSubject.replace("חיוב", "").trim();

    switch (invoiceType) {
      case `חשבונית מס`:
        message = `זיכוי חשבונית ${invoiceNumber} ${subjectContent}`;
        break;
      case `חשבונית מס / קבלה`:
        message = `זיכוי חשבונית ${invoiceNumber} ${subjectContent}`;
        break;
      case `קבלה`:
        message = `זיכוי קבלה ${invoiceNumber} ${subjectContent}`;
        break;
      default:
        message = `האם אתה בטוח שברצונך לזכות את ה-${invoiceType}?`;
        break;
    }

    descriptionForm.setValue(message);
    priceForm.setValue(invoicePrice);

    return message;
  }

  handleCancelTitle(): string {
    const invoiceType = this.selectedInvoice.TypeName;

    let message = ``;

    switch (invoiceType) {
      case `חשבונית מס`:
        message = `הפק חשבונית זיכוי`;
        break;
      case `חשבונית מס / קבלה`:
        message = `הפק חשבונית זיכוי והפקת קבלה בסכום שלילי`;
        break;
      case `קבלה`:
        message = `הפק קבלה בסכום שלילי`;
        break;
      default:
        message = `האם אתה בטוח שברצונך לזכות את ה-${invoiceType}?`;
        break;
    }

    return message;
  }
  handleCancelButton(): string {
    const invoiceType = this.selectedInvoice.TypeName;

    let message = ``;

    switch (invoiceType) {
      case `חשבונית מס`:
        message = `הפק חשבונית זיכוי`;
        break;
      case `חשבונית מס / קבלה`:
        message = `הפק חשבונית זיכוי והפקת קבלה בסכום שלילי`;
        break;
      case `קבלה`:
        message = `הפק קבלה בסכום שלילי`;
        break;
      default:
        message = `האם אתה בטוח שברצונך לזכות את ה-${invoiceType}?`;
        break;
    }

    return message;
  }

  setDefaultFormValues(): void {
    const defaultDocument = this.documentTypes[1].value;
    const defaultPaymentMethod = this.paymentMethods[2].value;
    this.invoiceForm.get("documentType").setValue(defaultDocument);
    this.invoiceForm.get("paymentMethod").setValue(defaultPaymentMethod);
  }

  adjustPrice(invoice: any): number {
    const subjectKeyword = "זיכוי חשבונית";
    const typeName = "קבלה";

    // Check if conditions are met
    if (invoice.Subject.includes(subjectKeyword) && invoice.TypeName === typeName) {
      return invoice.Price * -1; // Return negative price
    }
    return invoice.Price; // Otherwise, return the price as is
  }

  examTypeNameChange(event: Event = null): void {
    const selectEl = event.target as HTMLSelectElement;
    const selectedIndex = selectEl.selectedIndex;
    const examName = selectEl.options[selectedIndex].text;
    const autoIndex = (event.target as HTMLInputElement).value;
    const examType = selectEl.options[selectedIndex].getAttribute("data-examtype");
    const custom = selectEl.options[selectedIndex].getAttribute("data-custom");

    const description = {
      examName,
      examType,
      HMO: this.currentPatient.HMO,
      custom,
    };

    const customExamName = this.structureDescription(description);

    this.invoiceForm.get("examName").setValue(customExamName);
  }

  examChange(event: Event = null): void {
    let displayedText;
    let selectedAutoIndex;
    
    if (event) {
      const selectEl = event.target as HTMLSelectElement;
      const selectedIndex = selectEl.selectedIndex;
      const selectedOption = selectEl.options[selectedIndex];
      displayedText = selectedOption.text;
      selectedAutoIndex = (event.target as HTMLInputElement).value;
    }

    const selectedAutoIndexValue = selectedAutoIndex || this.invoiceForm.get("examType").value;
    
    for (let exam of this.availableExams) {
      if (exam.AutoIndex == selectedAutoIndexValue) {
        let trname = this.translate.instant("appointments.gui.exams." + exam.examType);
        if (trname.indexOf("appointments.gui.exams") > -1) {
          trname = exam.examType;
        }
        if (displayedText) {
          this.invoiceForm.get("examType_native").setValue(displayedText);
        } else {
          this.invoiceForm.get("examType_native").setValue(exam.examName ? exam.examName : trname);
        }
        return;
      }
    }

    this.invoiceForm.get("examType_native").setValue(this.invoiceForm.get("examType").value);
  }

  selectChange(): void {
    this.invoiceForm.get("examType").setValue("");
    this.invoiceForm.get("examName").setValue("");
  }

  creditPrice(changer: string): void {
    let price = this.invoiceForm.get("price").value;
    let money = this.invoiceForm.get("cash").value;
    let cc = this.invoiceForm.get("creditCard").get("ccAmt").value;
    let bankAmount = this.invoiceForm.get("bankTransfer").get("amount").value;

    if ((changer == "cc" && !cc) || (changer == "amount" && !bankAmount))
      //don't force when they delete the value
      return;
    if (price && money && !bankAmount && price > money) {
      this.invoiceForm
        .get("bankTransfer")
        .get("amount")
        .setValue(price - money);
    }
    if (price && bankAmount && !money && price > bankAmount) {
      this.invoiceForm.get("cash").setValue(price - bankAmount);
    }
    if (price && money && !cc && price > money)
      this.invoiceForm
        .get("creditCard")
        .get("ccAmt")
        .setValue(price - money);
    if (price && cc && !money && price > cc) this.invoiceForm.get("cash").setValue(price - cc);
  }

  calculate() {
    let price = this.invoiceForm.get("price").value;
    let money = this.invoiceForm.get("cash").value;
    if (this.isCashCard()) {
      this.invoiceForm
        .get("creditCard")
        .get("ccAmt")
        .setValue(price - money);
    } else if (this.isBankTransferCash()) {
      this.invoiceForm
        .get("bankTransfer")
        .get("amount")
        .setValue(price - money);
    } else {
      //cash + checks
      this.updateChecks(this.invoiceForm.get("numberOfPayments").value);
    }
    this.validateTotalAmount();
  }

  get isDoctorMissing(): boolean {
    return !this.invoiceForm.get("doctor").value;
  }

  get isExamTypeMissing(): boolean {
    return !this.invoiceForm.get("examType").value;
  }

  get isExamNameMissing(): boolean {
    return !this.invoiceForm.get("examName").value;
  }

  get isFirstNameMissing(): boolean {
    return !this.invoiceForm.get("firstName").value;
  }

  get isLastNameMissing(): boolean {
    // Last name is not required for business clients
    if (this.currentPatient?.isBusinessClient) {
      return false;
    }
    return !this.invoiceForm.get("lastName").value;
  }

  get isIDMissing(): boolean {
    return !this.invoiceForm.get("ID").value;
  }

  get isPriceMissing(): boolean {
    return !this.invoiceForm.get("price").value;
  }

  private normalizeToNoon(d: any): Date | null {
    if (!d) return null;

    if (moment.isMoment(d)) {
      return d.clone().hours(12).minutes(0).seconds(0).milliseconds(0).toDate();
    }

    const date = d instanceof Date ? new Date(d) : new Date(d);
    if (isNaN(date.getTime())) return null as any;

    date.setHours(12, 0, 0, 0);
    return date;
  }

  private normalizeAllPaymentDatesForSubmit(): void {
    // Checks
    this.checks.controls.forEach((ctrl) => {
      const raw = ctrl.get("date")?.value;
      const noon = this.normalizeToNoon(raw);
      ctrl.get("date")?.setValue(noon, { emitEvent: false });
    });

    // Bank transfer
    const bt = this.invoiceForm.get("bankTransfer") as UntypedFormGroup;
    if (bt?.get("date")?.value) {
      const noon = this.normalizeToNoon(bt.get("date")!.value);
      bt.get("date")!.setValue(noon, { emitEvent: false });
    }
  }

  // ===================== Generate Invoice from Receipt Methods =====================

  openGenerateInvoicePopup(invoice: any): void {
    this.selectedReceiptForInvoice = invoice;
    this.showGenerateInvoicePopup = true;
    // Try to load existing uninvoiced items first, then fall back to receipt itself
    this.loadReceiptChecks(invoice.AutoIndex);
  }

  closeGenerateInvoicePopup(): void {
    this.showGenerateInvoicePopup = false;
    this.selectedReceiptForInvoice = null;
    this.receiptChecks = [];
    this.loadingCheckId = null;
  }

  loadReceiptChecks(receiptAutoIndex: number): void {
    // Prevent multiple simultaneous requests for the same receipt
    if (this.receiptsLoadingInProgress.has(receiptAutoIndex)) {
      return;
    }
    
    this.receiptsLoadingInProgress.add(receiptAutoIndex);
    
    // Query to get uninvoiced checks or other items for this receipt
    this.billingService
      .getReceiptChecks(receiptAutoIndex)
      .subscribe(
        (response: any) => {
          this.receiptsLoadingInProgress.delete(receiptAutoIndex);
          
          // Filter only items without invoiceID (uninvoiced items like checks)
          let itemsWithoutInvoice = response.filter((item: any) => !item.invoiceID);
          
          // Update the map with status (true = has uninvoiced items)
          this.receiptUninvoicedChecksMap.set(receiptAutoIndex, itemsWithoutInvoice.length > 0);
          
          // Add the receipt itself as an option only if there are no uninvoiced items
          if (itemsWithoutInvoice.length === 0) {
            itemsWithoutInvoice.push({
              AutoIndex: receiptAutoIndex,
              recieptID: receiptAutoIndex,  // Use AutoIndex, not DocumentID
              Amount: this.selectedReceiptForInvoice.Price,
              Type: 'Receipt',
              checkDate: this.selectedReceiptForInvoice.Date || new Date()
            });
          }
          
          this.receiptChecks = itemsWithoutInvoice;
        },
        (error) => {
          this.receiptsLoadingInProgress.delete(receiptAutoIndex);
          console.error('✗ Failed to load receipt items:', error);
          // If there's an error, still offer the receipt itself as an option
          this.receiptChecks = [{
            AutoIndex: receiptAutoIndex,
            recieptID: receiptAutoIndex,  // Use AutoIndex, not DocumentID
            Amount: this.selectedReceiptForInvoice.Price,
            Type: 'Receipt',
            checkDate: this.selectedReceiptForInvoice.Date || new Date()
          }];
          this.receiptUninvoicedChecksMap.set(receiptAutoIndex, false);
        }
      );
  }

  hasUninvoicedChecks(invoice: any): boolean {
    // Only show button for receipts
    if (invoice.Type !== 400) {
      return false;
    }
    // Check if we have cached data for this receipt
    if (this.receiptUninvoicedChecksMap.has(invoice.AutoIndex)) {
      const hasUninvoiced = this.receiptUninvoicedChecksMap.get(invoice.AutoIndex);
      // Show button if there are uninvoiced items OR if receipt hasn't been invoiced yet
      return hasUninvoiced || !invoice.InvoicedFlag;
    }
    // If not cached, load and check
    this.loadReceiptChecks(invoice.AutoIndex);
    // Default to showing button until we know for sure
    return true;
  }

  /**
   * Route to the correct handler based on item type (check vs direct receipt)
   */
  generateInvoiceFromItem(item: any): void {
    if (item.Type === 'Receipt') {
      // Direct receipt invoice
      this.generateInvoiceFromReceipt(item);
    } else {
      // Check-based invoice
      this.generateInvoiceFromCheck(item);
    }
  }

  /**
   * Generate invoice from a check (check payment method)
   * Called only when item.Type !== 'Receipt'
   */
  generateInvoiceFromCheck(check: any): void {
    this.loadingCheckId = check.AutoIndex;
    const receiptAutoIndex = this.selectedReceiptForInvoice.AutoIndex;
    
    console.log(`[generateInvoiceFromCheck] Processing check:`, {
      checkAutoIndex: check.AutoIndex,
      receiptID: check.recieptID,
      checkType: check.Type,
      amount: check.amount
    });
    
    this.billingService
      .produceInvoiceFromCheck(check.recieptID, check.AutoIndex)
      .pipe(
        switchMap((response: any) => {
          this.resultMessage = `חשבונית #${response.number} הופקה בהצלחה`;
          this.result = true;
          this.loadingCheckId = null;
          this.closeGenerateInvoicePopup();
          
          // Clear the cache for this receipt since it changed
          this.receiptUninvoicedChecksMap.delete(receiptAutoIndex);
          
          return this.billingService.getInvoices(this.currentPatient.PID).pipe(
            delay(300) // Small delay to ensure DB is updated
          );
        })
      )
      .subscribe(
        (invoices) => {
          this.invoicesData = invoices;
        },
        (error) => {
          console.error('✗ Failed to generate invoice from check:', error);
          this.resultMessage = `שגיאה: ${error.error?.error || 'Failed to generate invoice'}`;
          this.result = true;
          this.loadingCheckId = null;
        }
      );
  }

  /**
   * Generate invoice from a direct receipt (non-check payment)
   * Called only when item.Type === 'Receipt'
   */
  generateInvoiceFromReceipt(receipt: any): void {
    this.loadingCheckId = receipt.AutoIndex;
    const receiptAutoIndex = this.selectedReceiptForInvoice.AutoIndex;
    
    console.log(`[generateInvoiceFromReceipt] Processing direct receipt:`, {
      receiptAutoIndex: receipt.AutoIndex,
      receiptID: receipt.recieptID,
      receiptType: receipt.Type,
      paymentMethod: this.selectedReceiptForInvoice.PaymentMethod,
      price: this.selectedReceiptForInvoice.Price
    });
    
    // For direct receipts, pass 0 as checkid to signal it's not a check
    this.billingService
      .produceInvoiceFromCheck(receipt.recieptID, 0)
      .pipe(
        switchMap((response: any) => {
          this.resultMessage = `חשבונית #${response.number} הופקה בהצלחה`;
          this.result = true;
          this.loadingCheckId = null;
          this.closeGenerateInvoicePopup();
          
          // Mark the receipt as invoiced
          if (this.selectedReceiptForInvoice) {
            this.selectedReceiptForInvoice.InvoicedFlag = true;
          }
          
          // Clear the cache for this receipt since it changed
          this.receiptUninvoicedChecksMap.delete(receiptAutoIndex);
          
          return this.billingService.getInvoices(this.currentPatient.PID).pipe(
            delay(300) // Small delay to ensure DB is updated
          );
        })
      )
      .subscribe(
        (invoices) => {
          this.invoicesData = invoices;
        },
        (error) => {
          console.error('✗ Failed to generate invoice from receipt:', error);
          this.resultMessage = `שגיאה: ${error.error?.error || 'Failed to generate invoice'}`;
          this.result = true;
          this.loadingCheckId = null;
        }
      );
  }

  async sendEmail(invoice: any) {
    const email = this.currentPatient?.Email;
    if (!email) {
      this.resultMessage = this.translate.instant('billing.gui.labels.missing email');
      this.result = true;
      return;
    }
    this.emailLoadingId = invoice.DocumentID;
    this.billingService.sendEmail(invoice.DocumentID, email, invoice.DoctorUID).subscribe(
      () => {
        this.emailLoadingId = null;
        this.resultMessage = `${this.translate.instant('billing.gui.labels.email success')} ${email}`;
        this.result = true;
      },
      () => {
        this.emailLoadingId = null;
      }
    );
  }
}


